import { ticketType } from "@agentcall/core/type"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { tenantApi } from "@/api/client/tenant"
import { useAdminList, useOwnerList } from "@/api/generated/endpoints/api"
import type {
  AdminList200AdminsItem,
  OwnerList200OwnersItem,
  TenantList200TenantsItem,
} from "@/api/generated/models"
import { Combobox, type ComboboxOption } from "@/components/ui/combobox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

// 型をエクスポート（FROM/TOは同じenum値なのでcontactFromCategoryを使用）
export type ContactCategory = ticketType.contactFromCategory

// 連絡者区分のオプション（緊急連絡センター以外）
const contactCategoryOptions = ticketType.CONTACT_CATEGORIES.filter(
  (item) => item.key !== "emergency"
).map((item) => ({
  value: item.key,
  label: item.value,
}))

// 全ての連絡者区分オプション
const allContactCategoryOptions = ticketType.CONTACT_CATEGORIES.map((item) => ({
  value: item.key,
  label: item.value,
}))

/** 連絡者タイプ（owner, tenant, または oc） */
export type PersonType = "owner" | "tenant" | "oc"

// ページネーション設定
const PAGE_SIZE = 20

interface ContactPersonSelectorProps {
  /** 連絡者の方向（FROM または TO） */
  direction: "from" | "to"
  /** 連絡者区分の値 */
  categoryValue: ContactCategory | ""
  /** 連絡者ID（オーナー/テナント/管理者のID） */
  personIdValue?: string
  /** その他テキスト */
  textValue?: string
  /** 連絡者区分変更時のコールバック */
  onCategoryChange: (value: ContactCategory | "") => void
  /** 連絡者ID変更時のコールバック */
  onPersonIdChange?: (value: string, type: PersonType) => void
  /** その他テキスト変更時のコールバック */
  onTextChange?: (value: string) => void
  /** 連絡者区分のエラーメッセージ */
  categoryError?: string
  /** 連絡者区分のプレースホルダー */
  categoryPlaceholder?: string
  /** 連絡者IDのプレースホルダー */
  personIdPlaceholder?: string
  /** その他テキストのプレースホルダー */
  textPlaceholder?: string
  /** 緊急連絡センターを含めるかどうか */
  includeEmergency?: boolean
  /** 無効状態 */
  disabled?: boolean
  /** CSSクラス */
  className?: string
  /** 選択済みオーナーID（契約者情報から自動設定する場合） */
  selectedOwnerId?: string
  /** 選択済みオーナー名（契約者情報から自動設定する場合） */
  selectedOwnerName?: string
  /** 選択済み賃借人ID（契約者情報から自動設定する場合） */
  selectedTenantId?: number
  /** 選択済み賃借人名（契約者情報から自動設定する場合） */
  selectedTenantName?: string
  /** ログイン中の管理者ID */
  currentAdminId?: string
  /** ログイン中の管理者名 */
  currentAdminName?: string
  /** その他テキストのエラーメッセージ */
  textError?: string
}

export function ContactPersonSelector({
  direction,
  categoryValue,
  personIdValue = "",
  textValue = "",
  onCategoryChange,
  onPersonIdChange,
  onTextChange,
  categoryError,
  categoryPlaceholder,
  personIdPlaceholder,
  textPlaceholder,
  includeEmergency = false,
  disabled = false,
  className,
  selectedOwnerId,
  selectedOwnerName,
  selectedTenantId,
  selectedTenantName,
  currentAdminId,
  currentAdminName,
  textError,
}: ContactPersonSelectorProps) {
  const directionLabel = direction === "from" ? "FROM" : "TO"
  const options = includeEmergency ? allContactCategoryOptions : contactCategoryOptions

  const defaultCategoryPlaceholder = categoryPlaceholder || `連絡者区分（${directionLabel}）`
  const defaultPersonIdPlaceholder = personIdPlaceholder || `連絡者（${directionLabel}）`
  const defaultTextPlaceholder = textPlaceholder || `その他（${directionLabel}）`

  // カテゴリ判定
  const isOwnerCategory = categoryValue === "owner"
  const isTenantCategory = categoryValue === "tenant"
  const isOcCategory = categoryValue === "oc"
  // サジェストはオーナー・賃借人・OCの場合のみ（入居者・業者等は除外）
  const showCombobox = isOwnerCategory || isTenantCategory || isOcCategory

  // オーナー用状態
  const [ownerSearch, setOwnerSearch] = useState("")
  const [ownerPage, setOwnerPage] = useState(1)
  const [ownerItems, setOwnerItems] = useState<OwnerList200OwnersItem[]>([])

  // 賃借人用状態
  const [tenantSearch, setTenantSearch] = useState("")
  const [tenantPage, setTenantPage] = useState(1)
  const [tenantItems, setTenantItems] = useState<TenantList200TenantsItem[]>([])

  // OC（管理者）用状態
  const [adminSearch, setAdminSearch] = useState("")
  const [adminPage, setAdminPage] = useState(1)
  const [adminItems, setAdminItems] = useState<AdminList200AdminsItem[]>([])

  // デバウンス用タイマー
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // デバウンスタイマーのクリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // オーナーAPI（選択済みオーナーがいる場合は無効化）
  const { data: ownersData, isLoading: isLoadingOwners } = useOwnerList(
    { nameLike: ownerSearch || undefined, page: ownerPage, limit: PAGE_SIZE },
    { query: { enabled: isOwnerCategory && !(selectedOwnerId && selectedOwnerName) } }
  )

  // 賃借人API（選択済み賃借人がいる場合は無効化）
  const { data: tenantsData, isLoading: isLoadingTenants } = tenantApi.useTenantList(
    { nameLike: tenantSearch || undefined, page: tenantPage, limit: PAGE_SIZE },
    {
      query: {
        enabled: isTenantCategory && !(selectedTenantId !== undefined && selectedTenantName),
      },
    }
  )

  // 管理者API（OC選択時）
  const { data: adminsData, isLoading: isLoadingAdmins } = useAdminList(
    { nameLike: adminSearch || undefined, page: adminPage, limit: PAGE_SIZE },
    { query: { enabled: isOcCategory } }
  )

  // オーナーデータ取得時にアイテムを蓄積
  useEffect(() => {
    if (ownersData?.owners) {
      setOwnerItems((prev) =>
        ownerPage === 1
          ? ownersData.owners
          : [...prev, ...ownersData.owners.filter((o) => !prev.some((p) => p.id === o.id))]
      )
    }
  }, [ownersData, ownerPage])

  // 賃借人データ取得時にアイテムを蓄積
  useEffect(() => {
    if (tenantsData?.tenants) {
      setTenantItems((prev) =>
        tenantPage === 1
          ? tenantsData.tenants
          : [...prev, ...tenantsData.tenants.filter((t) => !prev.some((p) => p.id === t.id))]
      )
    }
  }, [tenantsData, tenantPage])

  // 管理者データ取得時にアイテムを蓄積
  useEffect(() => {
    if (adminsData?.admins) {
      setAdminItems((prev) =>
        adminPage === 1
          ? adminsData.admins
          : [...prev, ...adminsData.admins.filter((a) => !prev.some((p) => p.id === a.id))]
      )
    }
  }, [adminsData, adminPage])

  // デバウンス付き検索ハンドラ
  const handleSearchChange = useCallback(
    (value: string) => {
      // 選択済み契約者がいる場合は検索を無効化
      if (
        (isOwnerCategory && selectedOwnerId && selectedOwnerName) ||
        (isTenantCategory && selectedTenantId !== undefined && selectedTenantName)
      ) {
        return
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        if (isOwnerCategory) {
          setOwnerSearch(value)
          setOwnerPage(1)
        } else if (isTenantCategory) {
          setTenantSearch(value)
          setTenantPage(1)
        } else if (isOcCategory) {
          setAdminSearch(value)
          setAdminPage(1)
        }
      }, 300)
    },
    [
      isOwnerCategory,
      isTenantCategory,
      isOcCategory,
      selectedOwnerId,
      selectedOwnerName,
      selectedTenantId,
      selectedTenantName,
    ]
  )

  // 追加読み込みハンドラ
  const handleLoadMore = useCallback(() => {
    // 選択済み契約者がいる場合は追加読み込みを無効化
    if (
      (isOwnerCategory && selectedOwnerId && selectedOwnerName) ||
      (isTenantCategory && selectedTenantId !== undefined && selectedTenantName)
    ) {
      return
    }
    if (isOwnerCategory) {
      setOwnerPage((p) => p + 1)
    } else if (isTenantCategory) {
      setTenantPage((p) => p + 1)
    } else if (isOcCategory) {
      setAdminPage((p) => p + 1)
    }
  }, [
    isOwnerCategory,
    isTenantCategory,
    isOcCategory,
    selectedOwnerId,
    selectedOwnerName,
    selectedTenantId,
    selectedTenantName,
  ])

  // Combobox開いた時のリセットハンドラ
  const handleOpen = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    if (isOwnerCategory) {
      setOwnerSearch("")
      setOwnerPage(1)
    } else if (isTenantCategory) {
      setTenantSearch("")
      setTenantPage(1)
    } else if (isOcCategory) {
      setAdminSearch("")
      setAdminPage(1)
    }
  }, [isOwnerCategory, isTenantCategory, isOcCategory])

  // カテゴリ変更時に検索状態をリセット（キャッシュされたアイテムは保持）
  const handleCategoryChange = useCallback(
    (value: ContactCategory | "") => {
      // 検索・ページ状態のみリセット（アイテムはキャッシュとして保持）
      setOwnerSearch("")
      setOwnerPage(1)
      setTenantSearch("")
      setTenantPage(1)
      setAdminSearch("")
      setAdminPage(1)
      onCategoryChange(value)
    },
    [onCategoryChange]
  )

  // オプション変換
  const ownerOptions = useMemo<ComboboxOption[]>(() => {
    // 選択済みオーナーがいる場合は、その情報のみを表示
    if (selectedOwnerId && selectedOwnerName) {
      return [
        {
          value: selectedOwnerId,
          label: selectedOwnerName,
        },
      ]
    }
    return ownerItems.map((owner) => ({
      value: owner.id,
      label: owner.name,
    }))
  }, [ownerItems, selectedOwnerId, selectedOwnerName])

  const tenantOptions = useMemo<ComboboxOption[]>(() => {
    // 選択済み賃借人がいる場合は、その情報のみを表示
    if (selectedTenantId !== undefined && selectedTenantName) {
      return [
        {
          value: selectedTenantId.toString(),
          label: selectedTenantName,
        },
      ]
    }
    return tenantItems.map((tenant) => ({
      value: tenant.id.toString(),
      label: tenant.name,
    }))
  }, [tenantItems, selectedTenantId, selectedTenantName])

  const adminOptions = useMemo<ComboboxOption[]>(() => {
    const options = adminItems.map((admin) => ({
      value: admin.id,
      label: admin.name,
    }))
    if (
      currentAdminId &&
      currentAdminName &&
      !options.some((admin) => admin.value === currentAdminId)
    ) {
      return [{ value: currentAdminId, label: currentAdminName }, ...options]
    }
    return options
  }, [adminItems, currentAdminId, currentAdminName])

  // 追加データ有無（選択済み契約者がいる場合は追加読み込み不可）
  const hasMoreOwners =
    selectedOwnerId && selectedOwnerName ? false : (ownersData?.owners?.length ?? 0) === PAGE_SIZE
  const hasMoreTenants =
    selectedTenantId !== undefined && selectedTenantName
      ? false
      : (tenantsData?.tenants?.length ?? 0) === PAGE_SIZE
  const hasMoreAdmins = (adminsData?.admins?.length ?? 0) === PAGE_SIZE

  // Combobox用のオプションとローディング状態
  const comboboxOptions = isOwnerCategory
    ? ownerOptions
    : isTenantCategory
      ? tenantOptions
      : isOcCategory
        ? adminOptions
        : []
  const isLoadingCombobox = isOwnerCategory
    ? isLoadingOwners
    : isTenantCategory
      ? isLoadingTenants
      : isOcCategory
        ? isLoadingAdmins
        : false
  const hasMore = isOwnerCategory
    ? hasMoreOwners
    : isTenantCategory
      ? hasMoreTenants
      : isOcCategory
        ? hasMoreAdmins
        : false

  const handlePersonIdChange = (value: string) => {
    const personType: PersonType = isOwnerCategory ? "owner" : isOcCategory ? "oc" : "tenant"
    onPersonIdChange?.(value, personType)
  }

  const renderPersonSelector = () => {
    if (showCombobox) {
      return (
        <Combobox
          value={personIdValue}
          onChange={handlePersonIdChange}
          options={comboboxOptions}
          placeholder={defaultPersonIdPlaceholder}
          searchPlaceholder="検索..."
          emptyMessage="該当なし"
          disabled={disabled || !categoryValue}
          className="w-full h-[32px]"
          onSearchChange={handleSearchChange}
          onLoadMore={handleLoadMore}
          isLoading={isLoadingCombobox}
          hasMore={hasMore}
          onOpen={handleOpen}
        />
      )
    }

    // 業者・その他の場合は常に非活性
    return (
      <Select
        value={personIdValue}
        onChange={(value) => {
          // 業者・その他の場合は適切なタイプを判定
          const personType: PersonType = isOcCategory ? "oc" : "owner"
          onPersonIdChange?.(value, personType)
        }}
        options={[]}
        placeholder={defaultPersonIdPlaceholder}
        disabled={true}
        className="w-full"
      />
    )
  }

  return (
    <div className={className}>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Select
            value={categoryValue}
            onChange={(value) => handleCategoryChange(value as ContactCategory | "")}
            options={options}
            placeholder={defaultCategoryPlaceholder}
            disabled={disabled}
            className="w-full"
          />
          {categoryError && <p className="text-xs text-red-500">{categoryError}</p>}
        </div>
        <div className="space-y-2">{renderPersonSelector()}</div>
        <div className="space-y-2">
          <Input
            value={textValue}
            onChange={(e) => onTextChange?.(e.target.value)}
            placeholder={defaultTextPlaceholder}
            disabled={disabled}
            className="w-full"
          />
          {textError && <p className="text-xs text-red-500">{textError}</p>}
        </div>
      </div>
    </div>
  )
}
